import React from 'react';

type ShareButtonsProps = {
  title: string;
  url: string;
  className?: string;
};

type ShareIconProps = {
  className?: string;
};

function WhatsappIcon({ className = '' }: ShareIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M19.05 4.91A9.82 9.82 0 0 0 12.02 2C6.58 2 2.15 6.42 2.15 11.86c0 1.74.45 3.44 1.31 4.95L2 22l5.34-1.4a9.83 9.83 0 0 0 4.68 1.19h.01c5.44 0 9.86-4.42 9.86-9.86a9.8 9.8 0 0 0-2.84-7.02Zm-7.03 15.21h-.01a8.17 8.17 0 0 1-4.16-1.14l-.3-.18-3.17.83.85-3.09-.2-.32a8.16 8.16 0 0 1-1.25-4.35c0-4.5 3.66-8.16 8.17-8.16a8.1 8.1 0 0 1 5.78 2.39 8.1 8.1 0 0 1 2.38 5.78c0 4.5-3.66 8.16-8.15 8.16Zm4.47-6.11c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1.03-.38-1.96-1.2-.72-.64-1.21-1.43-1.35-1.67-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.48-.4-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.33.98 2.49c.12.16 1.68 2.56 4.07 3.59.57.25 1.01.4 1.36.51.57.18 1.08.15 1.49.09.45-.07 1.43-.58 1.63-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z"
      />
    </svg>
  );
}

function FacebookIcon({ className = '' }: ShareIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M13.5 22v-8.2h2.76l.41-3.2H13.5V8.56c0-.93.26-1.56 1.59-1.56h1.69V4.13c-.29-.04-1.28-.13-2.43-.13-2.4 0-4.04 1.46-4.04 4.15v2.45H7.6v3.2h2.71V22h3.19Z"
      />
    </svg>
  );
}

function XIcon({ className = '' }: ShareIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M18.9 2H21l-6.56 7.5L22.16 22h-6.05l-4.74-6.2L5.95 22H3.84l7.01-8.01L1.44 2h6.2l4.28 5.66L18.9 2Zm-1.06 18.2h1.17L6.99 3.7H5.74L17.84 20.2Z"
      />
    </svg>
  );
}

function LinkedinIcon({ className = '' }: ShareIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M6.94 8.5H3.56V20h3.38V8.5Zm.22-3.56C7.15 3.87 6.38 3 5.28 3S3.4 3.87 3.4 4.94c0 1.05.75 1.94 1.84 1.94h.02c1.12 0 1.9-.89 1.9-1.94ZM20.6 13.12c0-3.46-1.85-5.07-4.32-5.07-1.99 0-2.88 1.1-3.37 1.87V8.5H9.53c.04.94 0 11.5 0 11.5h3.38v-6.42c0-.34.02-.67.12-.91.27-.67.89-1.36 1.93-1.36 1.36 0 1.9 1.03 1.9 2.55V20h3.38v-6.88Z"
      />
    </svg>
  );
}

function CopyIcon({ className = '' }: ShareIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M16 1H6a2 2 0 0 0-2 2v12h2V3h10V1Zm3 4H10a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H10V7h9v14Z"
      />
    </svg>
  );
}

export default function ContentShareButtons({
  title,
  url,
  className = '',
}: ShareButtonsProps) {
  const [copied, setCopied] = React.useState(false);

  const canUseClipboard =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard;

  const encodedUrl = encodeURIComponent(url || '');
  const encodedTitle = encodeURIComponent(title || '');

  const shareLinks = [
    {
      id: 'whatsapp',
      label: 'Compartilhar no WhatsApp',
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      Icon: WhatsappIcon,
    },
    {
      id: 'facebook',
      label: 'Compartilhar no Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: FacebookIcon,
    },
    {
      id: 'x',
      label: 'Compartilhar no X',
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      Icon: XIcon,
    },
    {
      id: 'linkedin',
      label: 'Compartilhar no LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: LinkedinIcon,
    },
  ];

  async function handleCopy() {
    if (!url) return;

    try {
      if (canUseClipboard) {
        await navigator.clipboard.writeText(url);
      } else if (typeof document !== 'undefined') {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }

      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error('Falha ao copiar link', error);
    }
  }

  return (
    <section
      className={`content-view__share ${className}`.trim()}
      aria-label="Compartilhe esta notícia"
    >
      <p className="content-view__share-title">Compartilhe</p>

      <div
        className="content-view__share-links"
        role="group"
        aria-label="Opções de compartilhamento"
      >
        {shareLinks.map(({ id, href, label, Icon }) => (
          <a
            key={id}
            href={href}
            className="content-view__share-link"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={label}
          >
            <Icon className="content-view__share-icon" />
          </a>
        ))}

        <button
          type="button"
          className="content-view__share-link"
          onClick={handleCopy}
          aria-label={copied ? 'Link copiado' : 'Copiar link da página'}
          title={copied ? 'Link copiado' : 'Copiar link da página'}
        >
          <CopyIcon className="content-view__share-icon" />
        </button>
      </div>
    </section>
  );
}
